<template>
    <div ref="container"
         class="marquee-container"
         :class="{ marquee: overflow }">
        <div ref="track"
             class="marquee-track"
             :style="trackStyle">

            <span class="marquee-text">
                {{ text }}
            </span>

            <span v-if="overflow"
                  class="marquee-text marquee-copy">
                {{ text }}
            </span>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from "vue";

const props = defineProps({
    text: {
        type: String,
        default: ""
    },
    speed: {
        type: Number,
        default: 70     // px/sec
    },
    gap: {
        type: Number,
        default: 40
    }
});

const container = ref(null);
const track = ref(null);

const overflow = ref(false);
const duration = ref(0);
const distance = ref(0);

let resizeObserver;

async function update() {
    await nextTick();

    if (!container.value || !track.value) return;

    const textWidth = track.value.children[0].scrollWidth;
    overflow.value = textWidth > container.value.clientWidth;

    if (!overflow.value) return;

    distance.value = textWidth + props.gap;

    duration.value = distance.value / props.speed;
}

watch(
    () => props.text,
    update
);

onMounted(async () => {
    await update();
    resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(container.value);
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
});

const trackStyle = computed(() => {
    if (!overflow.value) return {};

    return {
        "--distance": `${distance.value}px`,
        "--duration": `${duration.value}s`
    };
});
</script>

<style scoped>
    .marquee-container {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
    }

    .marquee-track {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
    }

.marquee-text {
    flex-shrink: 0;
    white-space: nowrap;
}

.marquee-copy {
    margin-left: v-bind('props.gap + "px"');
}

.marquee.marquee-container:hover .marquee-track {
    animation:
        marquee var(--duration)
        linear
        infinite;
}

@keyframes marquee {
    from {
        transform: translateX(0);
    }
    to {
        transform:
            translateX(calc(-1 * var(--distance)));
    }
}
</style>